import axios from "axios";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import "firebase/compat/firestore";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { catchExceptionCallback } from "resbutler-ui/error";
import { getConfig } from "../core/utilities";
import { setUser } from "../store/reducers/rootSlice";
import { readClaims, setActiveRestaurant } from "core/auth";

function useUser() {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = window.location.hash ? window.location.hash.slice(window.location.hash.indexOf("#") + 1).split("/") : [];
  const restaurantId = data[1];
  let date = data[0];
  if (!date) date = moment().format("YYYYMMDD");

  useEffect(() => {
    let unsubscribe = null as any;
    async function load() {
      try {
        setLoading(true);
        const data = getConfig();
        if (data) {
          if (firebase.apps.length) {
            await firebase.app().delete();
          }
          firebase.initializeApp(data.config);
          unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
            if (!user) {
              if (window.location.pathname !== "/login") window.location.href = `/login?returnUrl=${window.location.pathname}`;
              return;
            }
            setCurrentUser(user);
            const { tenantId, client } = getConfig();
            const forceDefault = !!window.sessionStorage.getItem("butler:forceDefaultRestaurant");
            if (forceDefault) window.sessionStorage.removeItem("butler:forceDefaultRestaurant");
            try {
              if (!getConfig()?.resbutlerApis) {
                // eslint-disable-next-line no-undef
                const { data: full } = await axios.get(`${import.meta.env.VITE_ADMIN_URL}/configByClient?clientId=${getConfig()?.client}`);
                window.localStorage.setItem("butler:data", JSON.stringify(full));
              }
              await setActiveRestaurant(forceDefault ? undefined : restaurantId || undefined);
            } catch (e) {
              catchExceptionCallback(e);
            }
            const claims = await readClaims();
            const [rtSnap, accessSnap] = await Promise.all([
              firebase.database().ref(`${tenantId}/users`).child(user.uid).once("value"),
              firebase.firestore().doc(`${client}/access/staff/${user.uid}`).get().catch(() => null),
            ]);
            const rtUser = rtSnap.exists() ? rtSnap.val() : null;
            const fsData = accessSnap?.exists ? (accessSnap.data() as any) : null;
            const fsUser = fsData
              ? { defaultRestaurant: fsData.defaultRestaurant, restaurants: fsData.restaurants || {}, tills: fsData.tills || {}, operatorId: fsData.operatorId, operatorLockTimeout: fsData.operatorLockTimeout, compactMaestroView: fsData.compactMaestroView }
              : null;
            const profile = (fsUser ?? rtUser) ?? {};
            dispatch(
              setUser(
                Object.assign({}, profile, {
                  phoneNumber: user.phoneNumber,
                  email: user.email,
                  displayName: user.displayName,
                  uid: user.uid,
                  customClaims: { roles: claims?.roles, grants: claims?.grants, activeRestaurant: claims?.activeRestaurant, restaurantIds: claims?.restaurantIds },
                })
              )
            );

            const effectiveRestaurantId = (forceDefault ? "" : restaurantId) || profile.defaultRestaurant || claims?.activeRestaurant || "";
            navigate(`${window.location.pathname}#${date}/${effectiveRestaurantId}`);
            setLoading(false);
          });
        } else {
          window.location.href = `/login?returnUrl=${window.location.pathname}`;
        }
      } catch (error) {
        catchExceptionCallback(error);
      }
    }
    load();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return [loading, currentUser];
}

export default useUser;
